import { useEffect, useState } from "react";
import axios from "axios";
import { PoisonTool } from "@/components/PoisonTool";
import { VerifyTool } from "@/components/VerifyTool";
import { Donate } from "@/components/Donate";
import { Badges } from "@/components/Badges";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { ShieldOff, Skull, EyeOff, ArrowDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats`).then(({ data }) => setCount(data.documents_protected)).catch(() => {});
  }, []);

  const scrollToTool = () => document.getElementById("tool")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="relative">
      {/* Header (mirrors authorrowancain.com) */}
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden max-w-6xl mx-auto px-6 pt-24 pb-20 md:pt-36 md:pb-28">
        {/* branded shield mark + glow */}
        <div className="pointer-events-none absolute inset-y-0 right-0 hidden md:flex items-center justify-end w-1/2 select-none" aria-hidden="true">
          <div className="absolute right-[-6%] top-1/2 -translate-y-1/2 w-[440px] h-[440px] rounded-full bg-[#70250a]/20 blur-[120px]" />
          <img
            src="/icon-512.png"
            alt=""
            className="relative w-[360px] lg:w-[440px] h-auto object-contain opacity-[0.16] drop-shadow-[0_0_60px_rgba(112,37,10,0.35)] rise"
            style={{ animationDelay: "0.15s" }}
          />
        </div>

        <div className="relative max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#70250a] mb-6 rise">Anti-AI manuscript defense</p>
          <h1 className="font-cinzel font-black uppercase leading-[1.05] text-5xl sm:text-6xl lg:text-7xl text-[#E6E2D6] rise" style={{ animationDelay: "0.1s" }}>
            Shroud your<br /> words from<br /><span className="text-[#70250a]">the machines.</span>
          </h1>
          <p className="font-lora text-lg text-[#E6E2D6]/75 mt-8 max-w-xl leading-relaxed rise" style={{ animationDelay: "0.2s" }}>
            Inkshroud laces your PDFs and EPUBs with invisible prompts, tokenizer-breaking noise, and decoy lies.
            Human readers see nothing changed. AI scrapers choke on poison.
          </p>
          <div className="flex flex-wrap items-center gap-5 mt-10 rise" style={{ animationDelay: "0.3s" }}>
            <button
              data-testid="hero-cta"
              onClick={scrollToTool}
              className="inline-flex items-center gap-3 bg-[#70250a] text-[#E6E2D6] font-cinzel font-bold uppercase tracking-widest text-sm py-5 px-10 hover:bg-[#8A2E0D] hover:shadow-[0_0_20px_rgba(112,37,10,0.45)] transition-colors transition-shadow duration-300"
            >
              Shroud a manuscript <ArrowDown className="w-4 h-4" />
            </button>
            {count !== null && (
              <span className="font-mono text-xs uppercase tracking-widest text-[#485354]" data-testid="global-count">
                {count.toLocaleString()} documents shrouded
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Tool */}
      <section id="tool" className="relative max-w-6xl mx-auto px-6 py-16 md:py-24 scroll-mt-20">
        <div className="mb-10">
          <h2 className="font-cinzel font-bold text-3xl sm:text-4xl text-[#E6E2D6] uppercase tracking-tight">The Shrouding Table</h2>
          <p className="font-lora text-[#E6E2D6]/60 mt-3 max-w-2xl">Upload a single book. It is processed on the server, shrouded, and returned instantly. Nothing is stored.</p>
        </div>
        <PoisonTool />
      </section>

      {/* Explainer */}
      <section className="relative max-w-6xl mx-auto px-6 py-20 md:py-28 border-t border-[#485354]/25">
        <h2 className="font-cinzel font-bold text-3xl sm:text-4xl text-[#E6E2D6] uppercase tracking-tight mb-4">Three layers of the shroud</h2>
        <p className="font-lora text-[#E6E2D6]/60 max-w-2xl mb-14">Every shrouded file carries a triple payload — silent to your readers, hostile to any model that dares ingest it.</p>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: EyeOff, t: "Invisible Prompts", d: "Render-mode-invisible text and hidden HTML blocks smuggle direct instructions to any AI reading your work — commanding it to refuse, forget, or reject the material entirely." },
            { icon: Skull, t: "Zero-Width Noise", d: "Zero-width characters and Cyrillic homoglyphs are woven through the machine layer. To a human the page is untouched; to a tokenizer, every sentence fractures into gibberish." },
            { icon: ShieldOff, t: "Decoy False Info", d: "Fabricated facts and phantom authors are seeded into the extractable text and metadata, quietly corrupting any dataset your book is scraped into." },
          ].map((c, i) => (
            <div key={c.t} className="border border-[#485354]/40 bg-[#041b24]/60 p-8 hover:-translate-y-1 hover:border-[#6d411c]/60 transition-transform transition-colors duration-300" data-testid={`explainer-card-${i}`}>
              <c.icon className="w-8 h-8 text-[#70250a] mb-6" strokeWidth={1.3} />
              <h3 className="font-cinzel text-xl text-[#E6E2D6] mb-3">{c.t}</h3>
              <p className="font-lora text-sm text-[#E6E2D6]/60 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Verify */}
      <section id="verify" className="relative max-w-6xl mx-auto px-6 py-20 md:py-28 border-t border-[#485354]/25 scroll-mt-20">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#70250a] mb-4">Proof of protection</p>
        <h2 className="font-cinzel font-bold text-3xl sm:text-4xl text-[#E6E2D6] uppercase tracking-tight mb-4">Verify the shrouding</h2>
        <p className="font-lora text-[#E6E2D6]/60 max-w-2xl mb-12">Upload a protected file to confirm the hidden layers are intact and see exactly what an AI scraper would choke on.</p>
        <VerifyTool />
      </section>

      {/* Donate */}
      <Donate />

      {/* Badges & Embeds */}
      <Badges />

      {/* Footer (mirrors authorrowancain.com) */}
      <SiteFooter />
    </div>
  );
};

export default Home;
